import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton
} from '@mui/material';
import MDButton from 'components/MDButton';
import MDBox from 'components/MDBox';
import Icon from '@mui/material/Icon';

/**
 * Modal de resultados: para mostrar los resultados de la carga masiva de candidatos es decir muestra cuántos candidatos se cargaron exitosamente, cuántos tuvieron errores y cuáles fueron esos errores.
 */
const BulkUploadResultModal = ({ open, onClose, results }) => {
  if (!results) return null;

  const { success, errors, duplicates, total } = results;
  const hasErrors = errors.length > 0;
  const hasDuplicates = duplicates && duplicates.length > 0;

  const getStatusColor = () => {
    if (errors.length === 0 && (!duplicates || duplicates.length === 0)) return 'success';
    if (success === 0) return 'error';
    return 'warning';
  };

  const getStatusIcon = () => {
    const color = getStatusColor();
    if (color === 'success') return 'check_circle';
    if (color === 'error') return 'error';
    return 'warning';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <MDBox display="flex" alignItems="center" gap={1}>
          <Icon color={getStatusColor()} fontSize="large">
            {getStatusIcon()}
          </Icon>
          <Typography variant="h5">
            Resultado de Carga Masiva
          </Typography>
        </MDBox>
      </DialogTitle>

      <DialogContent>
        {/* Resumen General */}
        <MDBox mb={3}>
          <Alert severity={getStatusColor()} variant="outlined">
            <Typography variant="h6" gutterBottom>
              Resumen de la Carga
            </Typography>
            <MDBox display="flex" gap={2} flexWrap="wrap" mt={2}>
              <Chip 
                icon={<Icon>upload</Icon>}
                label={`Total: ${total}`} 
                color="default" 
                variant="outlined"
              />
              <Chip 
                icon={<Icon>check_circle</Icon>}
                label={`Exitosos: ${success}`} 
                color="success"
              />
              {hasDuplicates && (
                <Chip 
                  icon={<Icon>content_copy</Icon>}
                  label={`Duplicados: ${duplicates.length}`} 
                  color="warning"
                />
              )}
              {hasErrors && (
                <Chip 
                  icon={<Icon>error</Icon>}
                  label={`Errores: ${errors.length}`} 
                  color="error"
                />
              )}
            </MDBox>
          </Alert>
        </MDBox>

        {/* Lista de Duplicados */}
        {hasDuplicates && (
          <MDBox mb={3}>
            <Typography variant="h6" color="warning.main" gutterBottom>
              <Icon sx={{ verticalAlign: 'middle', mr: 1 }}>content_copy</Icon>
              Candidatos Duplicados ({duplicates.length})
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              Los siguientes candidatos no fueron cargados porque ya existen en el sistema:
            </Typography>
            <List sx={{ 
              maxHeight: 200, 
              overflow: 'auto',
              bgcolor: 'warning.lighter',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'warning.light'
            }}>
              {duplicates.map((dup, index) => (
                <ListItem key={index} divider={index < duplicates.length - 1}>
                  <ListItemText
                    primary={
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <Chip 
                          label={`Fila ${dup.row}`} 
                          size="small" 
                          color="warning"
                        />
                        <Typography variant="body2" fontWeight="medium">
                          {dup.nombre}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Doc: {dup.documento}
                        </Typography>
                      </MDBox>
                    }
                    secondary={
                      <Typography variant="caption" color="warning.dark">
                        {dup.error}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </MDBox>
        )}

        {/* Lista de Errores */}
        {hasErrors && (
          <MDBox>
            <Typography variant="h6" color="error.main" gutterBottom>
              <Icon sx={{ verticalAlign: 'middle', mr: 1 }}>error</Icon>
              Errores Encontrados ({errors.length})
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              Los siguientes registros no pudieron ser procesados:
            </Typography>
            <List sx={{ 
              maxHeight: 200, 
              overflow: 'auto',
              bgcolor: 'error.lighter',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'error.light'
            }}>
              {errors.map((error, index) => (
                <ListItem key={index} divider={index < errors.length - 1}>
                  <ListItemText
                    primary={
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <Chip 
                          label={`Fila ${error.row}`} 
                          size="small" 
                          color="error"
                        />
                        <Typography variant="body2" fontWeight="medium">
                          {error.nombre}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Doc: {error.documento}
                        </Typography>
                      </MDBox>
                    }
                    secondary={
                      <Typography variant="caption" color="error.dark">
                        {error.error}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </MDBox>
        )}

        {/* Mensaje de éxito total */}
        {success === total && (
          <Alert severity="success">
            <Typography variant="body1">
              ¡Todos los candidatos fueron cargados exitosamente!
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <MDButton onClick={onClose} variant="gradient" color="info">
          Cerrar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

BulkUploadResultModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  results: PropTypes.shape({
    success: PropTypes.number.isRequired,
    errors: PropTypes.array.isRequired,
    duplicates: PropTypes.array,
    total: PropTypes.number.isRequired
  })
};

export default BulkUploadResultModal;